import React, { useEffect, useRef } from "react";

/**
 * cca1.js — Commercial Growth System landing page
 * Content and design sourced 1:1 from "COMMERCIAL GROWTH SYSTEM — DOCUMENT 01 — THE JOURNEY"
 *
 * Drop-in page component for a MERN app.
 * No external UI libraries required — styles are scoped in the injected
 * <style> tag below (id="cca1-styles"), so this file is self-contained.
 *
 * Usage (React Router example):
 *   import Cca1 from "./pages/cca1";
 *   <Route path="/growth-system" element={<Cca1 />} />
 */

const PAGES = [
  {
    num: "01",
    sidebarTitle: "THE DIAGNOSIS",
    sidebarBody:
      "Before we build, we diagnose. We identify exactly where money is falling through the cracks in your current process.",
    title: "The Architecture of Predictable Growth.",
    redHeadline:
      "You do not have a sales problem. You have a visibility problem.",
    content: (
      <>
        <p>
          Right now, there are businesses in your city that need exactly what
          you sell. They have the budget. They have the urgency. But when they
          look for a solution, they do not find you. They find your competitors.
        </p>
        <p>
          Your competitors are not winning because their product is superior.
          They are winning because their acquisition machine is louder. Your
          website is leaking traffic. Your pricing structure confuses prospects.
          Your follow-up process relies on manual effort and memory.
        </p>
        <p>
          <strong>This is the bottleneck.</strong> You are an expert at
          delivering your service, but you are forcing yourself to also be an
          expert at lead generation, copywriting, web development, and cold
          outreach.
        </p>
        <p>
          The solution is not to work harder. The solution is to install a
          machine that works for you.
        </p>
      </>
    ),
  },
  {
    num: "02",
    sidebarTitle: "THE EXECUTION",
    sidebarBody:
      "We do not consult. We execute. The entire technical and strategic build-out is handled by our team while you focus on your business.",
    title: "The Onboarding Protocol.",
    redHeadline:
      "Speed to impact. We engineer fast wins in the first 7 days.",
    content: (
      <>
        <p>
          The moment your commitment is finalized, our onboarding protocol
          begins. We do not ask you to fill out endless questionnaires. We
          schedule a single, highly structured deep-dive session to understand
          your business, your ideal client, and your operational constraints.
        </p>
        <div className="cca1-subsection">
          <h3 className="cca1-sub-h">1. BOTTLENECK ELIMINATION</h3>
          <p>
            We immediately identify the friction points preventing your current
            leads from converting. We restructure your pricing and offer to make
            saying "no" feel irrational to your prospects.
          </p>
        </div>
        <div className="cca1-subsection">
          <h3 className="cca1-sub-h">2. THE FAST WINS</h3>
          <p>
            Within days, we deploy rapid-deployment assets. We professionalize
            your social media presence, launch a high-converting sales funnel
            website, and establish your technical SEO foundation. We build the
            infrastructure of trust.
          </p>
        </div>
        <div className="cca1-subsection">
          <h3 className="cca1-sub-h">3. THE STRATEGY BRIEFING</h3>
          <p>
            Once the foundation is built, we hold a secondary briefing with you.
            We present the exact outreach architecture we have constructed. You
            approve the messaging. We turn the key.
          </p>
        </div>
      </>
    ),
  },
  {
    num: "03",
    sidebarTitle: "THE NETWORK",
    sidebarBody:
      "Leverage our existing relationships with property managers, builders, and commercial directors to bypass the gatekeepers.",
    title: "The Acquisition Network.",
    redHeadline: "We do not guess. We leverage established B2B channels.",
    content: (
      <>
        <p>
          Cold outreach fails when it lacks context and authority. Our
          commercial client acquisition system bypasses the traditional
          cold-calling grind.
        </p>
        <p>
          We operate a massive, established network of B2B relationships. We are
          already connected with property detailers, facility managers, roofing
          companies, and commercial real estate directors. When we run a campaign
          for you, we are not knocking on locked doors. We are walking you
          through doors we have already opened.
        </p>
        <p>
          <strong>The Omnipresence Approach:</strong> We deploy simultaneous,
          synchronized outreach across SMS, email, and direct phone contact. When
          a prospect goes quiet, our automated reactivation sequences bring them
          back to the table.
        </p>
        <p>
          The result is a calendar populated with highly qualified, commercial
          decision-makers who are expecting your call.
        </p>
      </>
    ),
  },
  {
    num: "04",
    sidebarTitle: "THE FILTER",
    sidebarBody:
      "We only work with operators who are ready to scale. The nominal upfront fee ensures we only deploy our resources for serious partners.",
    title: "The Commitment Filter.",
    redHeadline: "The campaign is free. The fee is a filter.",
    content: (
      <>
        <p>
          We invest significant capital, human resources, and proprietary network
          access into every campaign we run. The actual cost of deploying this
          infrastructure is in the thousands.
        </p>
        <p>
          The $47, $197, or $450 charges are not the price of the campaign.{" "}
          <strong>
            They are service charges designed as a commitment filter.
          </strong>
        </p>
        <p>
          This is a Test Pilot Campaign. We are evaluating you just as much as
          you are evaluating us. We only partner with businesses that are
          serious, prepared for scale, and ready to handle commercial volume. If
          a business owner hesitates at a $47 commitment to completely rebuild
          their acquisition system, they do not have the operational maturity to
          handle the commercial contracts we will deliver.
        </p>
        <div className="cca1-objective-box">
          <span className="cca1-objective-eyebrow">THE ULTIMATE OBJECTIVE</span>
          <p className="cca1-objective-text">
            We are not selling a one-time service. We are auditioning you for a
            long-term, highly profitable partnership. We deliver the results
            upfront to prove our capability. If you can close the deals we put
            on your calendar, we scale together.
          </p>
        </div>
      </>
    ),
  },
];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.classList.add("cca1-in");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("cca1-in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

function PageSection({ page }) {
  const ref = useReveal();
  return (
    <div className="cca1-page cca1-reveal" ref={ref}>
      {/* Large title */}
      <div className="cca1-title-area">
        <h2 className="cca1-page-title">{page.title}</h2>
      </div>

      {/* Divider */}
      <div className="cca1-divider" />

      {/* Two-column body */}
      <div className="cca1-body-grid">
        {/* Left: main content */}
        <div className="cca1-main-col">
          <h3 className="cca1-red-headline">{page.redHeadline}</h3>
          <div className="cca1-body-text">{page.content}</div>
        </div>

        {/* Vertical rule */}
        <div className="cca1-vrule" aria-hidden="true" />

        {/* Right: sidebar */}
        <div className="cca1-sidebar">
          <div className="cca1-sidebar-num">{page.num}</div>
          <div className="cca1-sidebar-title">{page.sidebarTitle}</div>
          <p className="cca1-sidebar-body">{page.sidebarBody}</p>
        </div>
      </div>
    </div>
  );
}

export default function Cca1() {
  return (
    <div className="cca1-root">
      <style id="cca1-styles">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        /* ===== ROOT ===== */
        .cca1-root {
          background: #ffffff;
          color: #111111;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
          line-height: 1.6;
          overflow-x: hidden;
        }
        .cca1-root * { box-sizing: border-box; margin: 0; padding: 0; }

        /* ===== REVEAL ANIMATION ===== */
        .cca1-reveal {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.65s ease, transform 0.65s cubic-bezier(0.2, 0.7, 0.3, 1);
        }
        .cca1-reveal.cca1-in {
          opacity: 1;
          transform: translateY(0);
        }

        /* ===== PAGE SECTION ===== */
        .cca1-page {
          max-width: 900px;
          margin: 0 auto;
          padding: 80px 40px;
          border-bottom: 1px solid #e8e8e8;
        }
        .cca1-page:last-child {
          border-bottom: none;
        }

        /* ===== TITLE AREA ===== */
        .cca1-title-area {
          padding-bottom: 36px;
        }
        .cca1-page-title {
          font-size: clamp(36px, 6vw, 64px);
          font-weight: 800;
          line-height: 1.05;
          letter-spacing: -0.02em;
          color: #111111;
          max-width: 14ch;
        }

        /* ===== DIVIDER ===== */
        .cca1-divider {
          height: 1px;
          background: #111111;
          margin-bottom: 48px;
        }

        /* ===== BODY GRID ===== */
        .cca1-body-grid {
          display: grid;
          grid-template-columns: 1fr 1px 220px;
          gap: 0 40px;
          align-items: start;
        }

        /* ===== MAIN COLUMN ===== */
        .cca1-main-col {
          padding-right: 8px;
        }
        .cca1-red-headline {
          font-size: clamp(20px, 2.8vw, 26px);
          font-weight: 700;
          line-height: 1.25;
          color: #d0201a;
          margin-bottom: 28px;
        }
        .cca1-body-text p {
          font-size: 15px;
          line-height: 1.75;
          color: #333333;
          margin-bottom: 16px;
        }
        .cca1-body-text p:last-child { margin-bottom: 0; }
        .cca1-body-text strong { font-weight: 700; color: #111111; }

        /* ===== SUBSECTIONS (phase 2) ===== */
        .cca1-subsection {
          margin-top: 20px;
          margin-bottom: 4px;
        }
        .cca1-sub-h {
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 0.07em;
          color: #111111;
          margin-bottom: 8px;
          text-transform: uppercase;
        }
        .cca1-subsection p {
          font-size: 15px;
          line-height: 1.75;
          color: #333333;
          margin-bottom: 0 !important;
        }

        /* ===== VERTICAL RULE ===== */
        .cca1-vrule {
          width: 1px;
          background: #cccccc;
          align-self: stretch;
          min-height: 180px;
        }

        /* ===== SIDEBAR ===== */
        .cca1-sidebar {
          padding-left: 8px;
        }
        .cca1-sidebar-num {
          font-size: clamp(64px, 9vw, 96px);
          font-weight: 900;
          line-height: 0.9;
          color: #d0201a;
          letter-spacing: -0.03em;
          margin-bottom: 14px;
        }
        .cca1-sidebar-title {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #111111;
          margin-bottom: 12px;
        }
        .cca1-sidebar-body {
          font-size: 14px;
          line-height: 1.7;
          color: #555555;
        }

        /* ===== OBJECTIVE BOX (phase 4) ===== */
        .cca1-objective-box {
          background: #d0201a;
          padding: 28px 30px;
          margin-top: 28px;
        }
        .cca1-objective-eyebrow {
          display: block;
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #ffffff;
          margin-bottom: 14px;
        }
        .cca1-objective-text {
          font-size: 15px;
          line-height: 1.75;
          color: #ffffff;
          margin-bottom: 0 !important;
        }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 700px) {
          .cca1-page { padding: 56px 20px; }

          .cca1-body-grid {
            grid-template-columns: 1fr;
            gap: 36px 0;
          }
          .cca1-vrule { display: none; }
          .cca1-sidebar {
            padding-left: 0;
            border-top: 1px solid #e0e0e0;
            padding-top: 28px;
          }
          .cca1-sidebar-num { font-size: 72px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .cca1-reveal {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
      `}</style>

      {PAGES.map((page) => (
        <PageSection page={page} key={page.num} />
      ))}
    </div>
  );
}
